import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronRight, Play, ArrowRight, Check, Star, Users, BookOpen, Briefcase, Award, Calendar, MapPin, Clock, Search, Filter, Code, Cpu, Shield, BarChart, Cloud, Palette, Globe } from 'lucide-react';
import HeroSection from './HeroSection';

const CybelleLandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setScrolled(scrollY > 50);

      const sections = ['home', 'pillars', 'courses', 'programs', 'stories', 'events', 'resources', 'about'];
      const current = sections.find(section => {
        const el = document.getElementById(section);
        if (el) {
          const top = el.offsetTop - 100;
          const bottom = top + el.clientHeight;
          return scrollY >= top && scrollY < bottom;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const programs = [
    {
      id: 1,
      title: 'Full-Stack Development Bootcamp',
      category: 'training',
      duration: '16 weeks',
      level: 'Beginner to Advanced',
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop',
      spots: '24 spots available',
      startDate: 'Feb 2024'
    },
    {
      id: 2,
      title: 'Cybersecurity Fundamentals',
      category: 'training',
      duration: '12 weeks',
      level: 'Intermediate',
      image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&h=600&fit=crop',
      spots: '18 spots available',
      startDate: 'Mar 2024'
    },
    {
      id: 3,
      title: 'Leadership in Tech',
      category: 'mentorship',
      duration: '8 weeks',
      level: 'All Levels',
      image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=800&h=600&fit=crop',
      spots: '30 spots available',
      startDate: 'Jan 2024'
    }
  ];

  const stories = [
    {
      name: 'Phillipa EVE',
      role: 'Senior Software Engineer at Microsoft',
      image: '/cybelle/php.png',
      quote: 'CYBELLE gave me the confidence and skills to transition into tech. The mentorship program connected me with industry leaders who guided my career path.',
      achievement: 'Career transition in 6 months'
    },
    {
      name: 'Ana Dorine',
      role: 'Cybersecurity Analyst at IBM',
      image: '/cybelle/ana.png',
      quote: 'The hands-on training and real-world projects prepared me for the challenges in cybersecurity. I found my passion and my career here.',
      achievement: '3x salary increase'
    },
    {
      name: 'The geekette nerd',
      role: 'Product Manager at Google',
      image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT6AfUI4OT_Yfe_CkEq0FMJMuRlso-dRk3ZeA&s',
      quote: 'CYBELLE is more than education it is a community. The networking opportunities opened doors I never imagined possible.',
      achievement: 'Leading team of 12 engineers'
    }
  ];

  const events = [
    {
      title: 'Women in Tech Summit 2024',
      date: 'Jan 15, 2024',
      time: '9:00 AM - 5:00 PM',
      location: 'Virtual Conference',
      type: 'Conference',
      attendees: '500+',
      image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=500&fit=crop'
    },
    {
      title: 'AI & Machine Learning Workshop',
      date: 'Jan 22, 2024',
      time: '2:00 PM - 6:00 PM',
      location: 'San Francisco, CA',
      type: 'Workshop',
      attendees: '50',
      image: 'https://chambermade.app/wp-content/uploads/2024/03/411398994_372450818711714_5626500542538111621_n-1707x1280.jpg'
    },
    {
      title: 'Career Fair: Tech Companies Hiring',
      date: 'Feb 5, 2024',
      time: '10:00 AM - 4:00 PM',
      location: 'New York, NY',
      type: 'Career Fair',
      attendees: '200+',
      image: 'https://tech-summit.org/wp-content/uploads/blog-images/2024/nov/beyond-resume/booth-at-tech-jobs-fair.png'
    }
  ];

  const resources = [
    {
      title: 'Breaking Barriers: Women in Software Engineering',
      category: 'Article',
      readTime: '8 min read',
      date: 'Dec 15, 2023',
      image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=600&h=400&fit=crop',
      author: 'Dr. Amanda Foster'
    },
    {
      title: 'Salary Negotiation Strategies for Tech Professionals',
      category: 'Guide',
      readTime: '12 min read',
      date: 'Dec 10, 2023',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop',
      author: 'Lisa Patterson'
    },
    {
      title: 'Building Your Personal Brand in Tech',
      category: 'Video',
      readTime: '15 min watch',
      date: 'Dec 5, 2023',
      image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&h=400&fit=crop',
      author: 'Rachel Kim'
    }
  ];

  const stats = [
    { value: '5,000+', label: 'Women Trained' },
    { value: '87%', label: 'Job Placement Rate' },
    { value: '200+', label: 'Partner Companies' },
    { value: '40+', label: 'Countries Reached' }
  ];

  const pillars = [
    {
      title: 'Digital Skills Training',
      description: 'Coding, AI, cybersecurity, cloud, UI/UX',
      icon: Code,
      color: 'text-blue-500'
    },
    {
      title: 'Mentorship',
      description: 'Guidance from experienced women in tech',
      icon: Users,
      color: 'text-green-500'
    },
    {
      title: 'Career Pathways',
      description: 'Job placements, internship connections',
      icon: Briefcase,
      color: 'text-purple-500'
    },
    {
      title: 'Entrepreneurship Support',
      description: 'Funding, pitch sessions, startup incubation',
      icon: Award,
      color: 'text-red-500'
    },
    {
      title: 'Networking & Community',
      description: 'Women-led tech community meetups',
      icon: Globe,
      color: 'text-yellow-500'
    },
    {
      title: 'Scholarships & Grants',
      description: 'Support for disadvantaged participants',
      icon: BookOpen,
      color: 'text-indigo-500'
    }
  ];

  const courses = [
    {
      title: 'Software Development',
      icon: Code,
      color: 'text-blue-500',
      rating: 4.8,
      image: '/cybelle/code.png'
    },
    {
      title: 'Data & Analytics',
      icon: BarChart,
      color: 'text-green-500',
      rating: 4.7,
      image: 'https://online.hbs.edu/Style%20Library/api/resize.aspx?imgpath=/online/PublishingImages/blog/posts/business-professional-examining-data-analytics.jpg&w=1200&h=630'
    },
    {
      title: 'Cybersecurity',
      icon: Shield,
      color: 'text-red-500',
      rating: 4.9,
      image: 'https://www.shutterstock.com/image-photo/portrait-smiling-female-technician-server-600nw-2232740227.jpg'
    },
    {
      title: 'Digital Marketing',
      icon: Cpu,
      color: 'text-purple-500',
      rating: 4.6,
      image: 'https://thumbs.dreamstime.com/b/african-woman-using-social-media-tablet-sitting-cafe-american-digital-to-promote-her-e-business-indoors-internet-marketing-200433494.jpg'
    },
    {
      title: 'AI & Cloud Computing',
      icon: Cloud,
      color: 'text-yellow-500',
      rating: 4.8,
      image: 'https://img.freepik.com/free-photo/data-center-software-developers-using-pc-visualize-ai-neural-networks_482257-126785.jpg?semt=ais_hybrid&w=740&q=80'
    },
    {
      title: 'UI/UX Design',
      icon: Palette,
      color: 'text-indigo-500',
      rating: 4.7,
      image: 'https://media.licdn.com/dms/image/v2/D4D22AQGenSgZGKbqrA/feedshare-shrink_800/feedshare-shrink_800/0/1703749556795?e=2147483647&v=beta&t=DhDnCY9n5Y30ctikVZkB1aJWehP_ffd6_4X9sga_1vc'
    }
  ];

  const partners = [
    { name: 'Microsoft', logo: 'https://logo.clearbit.com/microsoft.com' },
    { name: 'Google', logo: 'https://logo.clearbit.com/google.com' },
    { name: 'Amazon', logo: 'https://logo.clearbit.com/amazon.com' },
    //{ name: 'IBM', logo: 'https://logo.clearbit.com/ibm.com' },
    { name: 'Meta', logo: 'https://logo.clearbit.com/meta.com' },
    { name: 'Apple', logo: 'https://logo.clearbit.com/apple.com' },
    { name: 'Salesforce', logo: 'https://logo.clearbit.com/salesforce.com' },
    //{ name: 'Adobe', logo: 'https://logo.clearbit.com/adobe.com' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <img 
                  src="/cybelle/logo.png" 
                  alt=" Logo" 
                  className="h-24 w-auto"
                />                
              </div>
            </div>
            
            <div className="hidden md:block">
              <div className="flex items-center gap-8">
                <a 
                  href="#home" 
                  className={`text-sm font-medium transition-colors ${activeSection === 'home' ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'}`}
                >
                  Home
                </a>
                <a 
                  href="#pillars" 
                  className={`text-sm font-medium transition-colors ${activeSection === 'pillars' ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'}`}
                >
                  Pillars
                </a>
                <a 
                  href="#courses" 
                  className={`text-sm font-medium transition-colors ${activeSection === 'courses' ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'}`}
                >
                  Courses
                </a>
                <a 
                  href="#programs" 
                  className={`text-sm font-medium transition-colors ${activeSection === 'programs' ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'}`}
                >
                  Programs
                </a>
                <a 
                  href="#stories" 
                  className={`text-sm font-medium transition-colors ${activeSection === 'stories' ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'}`}
                >
                  Success Stories
                </a>
                <a 
                  href="#events" 
                  className={`text-sm font-medium transition-colors ${activeSection === 'events' ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'}`}
                >
                  Events
                </a>
                <a 
                  href="#resources" 
                  className={`text-sm font-medium transition-colors ${activeSection === 'resources' ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'}`}
                >
                  Resources
                </a>
                <a 
                  href="#about" 
                  className={`text-sm font-medium transition-colors ${activeSection === 'about' ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'}`}
                >
                  About
                </a>
                <button className="px-5 py-2 text-sm font-semibold text-white bg-gray-900 rounded hover:bg-gray-800 transition-colors">
                  Get Started
                </button>
              </div>
            </div>
            
            <div className="md:hidden">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-gray-700">
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
        
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-3 space-y-3">
              <a href="#home" className="block text-sm font-medium text-gray-700">Home</a>
              <a href="#pillars" className="block text-sm font-medium text-gray-700">Pillars</a>
              <a href="#courses" className="block text-sm font-medium text-gray-700">Courses</a>
              <a href="#programs" className="block text-sm font-medium text-gray-700">Programs</a>
              <a href="#stories" className="block text-sm font-medium text-gray-700">Success Stories</a>
              <a href="#events" className="block text-sm font-medium text-gray-700">Events</a>
              <a href="#resources" className="block text-sm font-medium text-gray-700">Resources</a>
              <a href="#about" className="block text-sm font-medium text-gray-700">About</a>
              <button className="w-full px-5 py-2 text-sm font-semibold text-white bg-gray-900 rounded">
                Get Started
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Magazine Layout with Featured Content */}
      <HeroSection />

      {/* Pillars Section */}
      <section id="pillars" className="py-16 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Our Pillars</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              What we offer to empower women in tech
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => (
              <div 
                key={index} 
                className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="flex justify-center mb-4">
                  <pillar.icon className={`w-12 h-12 ${pillar.color} transition-transform duration-300 hover:rotate-12`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">{pillar.title}</h3>
                <p className="text-gray-600 text-center">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Courses Section */}
      <section id="courses" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Featured Courses & Tracks</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Explore our key training paths designed for your success in tech
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course, index) => (
              <div 
                key={index} 
                className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <img
                  src={course.image}
                  alt={course.title}
                  className="w-full h-48 object-cover transition-transform duration-500 hover:scale-105"
                />
                <div className="p-6">
                  <div className="flex justify-center mb-3">
                    <course.icon className={`w-8 h-8 ${course.color}`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">{course.title}</h3>
                  <div className="flex justify-center items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-4 h-4 ${i < Math.floor(course.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
                      />
                    ))}
                    <span className="text-sm text-gray-600 ml-2">({course.rating})</span>
                  </div>
                  <button className="w-full px-4 py-2 text-sm font-semibold text-gray-900 bg-gray-100 rounded hover:bg-gray-200 transition-colors">
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs" className="py-16 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Our Programs</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive training and mentorship programs designed to accelerate your tech career
            </p>
          </div>
          <div className="flex justify-center gap-3 mb-8 flex-wrap">
            {['all', 'training', 'mentorship', 'career'].map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                  selectedCategory === cat
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {programs
              .filter(program => selectedCategory === 'all' || program.category === selectedCategory)
              .map((program) => (
                <div key={program.id} className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={program.image}
                      alt={program.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                    <div className="absolute top-3 right-3">
                      <span className="px-3 py-1 text-xs font-semibold bg-white text-gray-900 rounded">
                        {program.category.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{program.title}</h3>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span>{program.duration}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <BookOpen className="w-4 h-4" />
                        <span>{program.level}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>Starts {program.startDate}</span>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">{program.spots}</span>
                        <button className="inline-flex items-center gap-1 text-sm font-semibold text-gray-900 hover:gap-2 transition-all">
                          Learn More
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Success Stories Section - Carousel Style */}
      <section id="stories" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Success Stories</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Real women, real transformations, real impact in the tech industry
            </p>
          </div>
          <div className="overflow-x-auto flex gap-8 pb-4 snap-x snap-mandatory">
            {stories.map((story, index) => (
              <div 
                key={index} 
                className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 min-w-[300px] snap-center"
              >
                <div className="flex items-start gap-4 mb-4">
                  <img
                    src={story.image}
                    alt={story.name}
                    className="w-16 h-16 rounded-full object-cover transition-transform duration-300 hover:scale-110"
                  />
                  <div>
                    <h3 className="font-bold text-gray-900">{story.name}</h3>
                    <p className="text-sm text-gray-600">{story.role}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed mb-4 italic">"{story.quote}"</p>
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <Check className="w-4 h-4 text-green-600" />
                    {story.achievement}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Events Section - Magazine Grid Layout with Animations */}
      <section id="events" className="py-16 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Upcoming Events</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Join our community events, workshops, and networking opportunities
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Featured Event - Large */}
            <div className="md:row-span-2">
              <div className="bg-white rounded-lg overflow-hidden border border-gray-200 h-full hover:shadow-lg transition-all duration-300">
                <div className="relative h-64 md:h-80 overflow-hidden">
                  <img
                    src={events[0].image}
                    alt={events[0].title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 text-xs font-semibold bg-white text-gray-900 rounded">
                      FEATURED EVENT
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 text-xs font-semibold bg-gray-100 text-gray-700 rounded">
                      {events[0].type}
                    </span>
                    <span className="flex items-center gap-1 text-sm text-gray-600">
                      <Users className="w-4 h-4" />
                      {events[0].attendees} attendees
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{events[0].title}</h3>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>{events[0].date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{events[0].time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span>{events[0].location}</span>
                    </div>
                  </div>
                  <button className="w-full px-4 py-3 text-sm font-semibold text-white bg-gray-900 rounded hover:bg-gray-800 transition-colors">
                    Register Now
                  </button>
                </div>
              </div>
            </div>
            {/* Regular Events */}
            {events.slice(1).map((event, index) => (
              <div key={index} className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300">
                <div className="grid grid-cols-5 h-full">
                  <div className="col-span-2 relative overflow-hidden">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                  <div className="col-span-3 p-5 flex flex-col justify-between">
                    <div>
                      <span className="inline-block px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-700 rounded mb-2">
                        {event.type}
                      </span>
                      <h3 className="text-lg font-bold text-gray-900 mb-3 leading-tight">{event.title}</h3>
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>{event.date}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <MapPin className="w-3.5 h-3.5" />
                          <span>{event.location}</span>
                        </div>
                      </div>
                    </div>
                    <button className="w-full px-3 py-2 text-sm font-semibold text-gray-900 bg-gray-100 rounded hover:bg-gray-200 transition-colors mt-4">
                      Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Section - Blog/Magazine Style with Carousel */}
      <section id="resources" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Resources & Insights</h2>
              <p className="text-gray-600">Expert advice, guides, and industry insights</p>
            </div>
            <button className="hidden md:inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-900 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors">
              View All
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="overflow-x-auto flex gap-6 pb-4 snap-x snap-mandatory">
            {resources.map((resource, index) => (
              <article 
                key={index} 
                className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300 min-w-[300px] snap-center group cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={resource.image}
                    alt={resource.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {resource.category === 'Video' && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <Play className="w-12 h-12 text-white opacity-90 group-hover:scale-110 transition-transform" />
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-700 rounded">
                      {resource.category}
                    </span>
                    <span className="text-xs text-gray-500">{resource.readTime}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight group-hover:text-gray-700 transition-colors">
                    {resource.title}
                  </h3>
                  <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                    <span className="text-xs text-gray-600">{resource.author}</span>
                    <span className="text-xs text-gray-500">{resource.date}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gray-900 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Your ICT Journey?
          </h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of women shaping the future of technology.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-3 text-sm font-semibold text-gray-900 bg-white rounded hover:bg-gray-100 transition-colors">
              Explore Programs
            </button>
            <button className="px-8 py-3 text-sm font-semibold text-white bg-transparent border-2 border-white rounded hover:bg-white hover:text-gray-900 transition-colors">
              Schedule a Call
            </button>
          </div>
        </div>
      </section>

      {/* Partners - with Colored Logos */}
      <section className="py-12 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-sm font-semibold text-gray-500 mb-8 uppercase tracking-wider">
            Trusted by Leading Tech Companies
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
            {partners.map((partner, index) => (
              <div key={index} className="text-center">
                <img 
                  src={partner.logo} 
                  alt={partner.name} 
                  className="h-12 mx-auto transition-transform duration-300 hover:scale-110" 
                  onError={(e) => { e.target.src = `https://via.placeholder.com/100x40?text=${partner.name}`; }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section - Enhanced with More Content */}
      <section id="about" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                About CYBELLE
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Founded in 2025, CYBELLE has become the leading platform dedicated to empowering women in the technology sector. We believe that diversity drives innovation, and our mission is to create pathways for women to excel in IT careers.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Through comprehensive training programs, personalized mentorship, and a supportive community, we have helped thousands of women transition into tech roles, advance their careers, and become leaders in the industry.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Our global reach spans over 40 countries, with partnerships from top tech companies. We offer flexible learning options, including online and hybrid models, to accommodate diverse schedules and backgrounds.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-gray-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Industry-Led Curriculum</h4>
                    <p className="text-sm text-gray-600">Courses designed by tech professionals from leading companies</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-gray-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Career Support</h4>
                    <p className="text-sm text-gray-600">Resume reviews, interview prep, and direct connections to hiring companies</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-gray-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Lifetime Community Access</h4>
                    <p className="text-sm text-gray-600">Join a network of professionals who support each other throughout their careers</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-gray-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Inclusive Scholarships</h4>
                    <p className="text-sm text-gray-600">Financial aid and grants for underrepresented groups in tech</p>
                  </div>
                </div>
              </div>
              <button className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-gray-900 rounded hover:bg-gray-800 transition-colors">
                Learn More About Us
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <img
                    src="/cybelle/co.png"
                    alt="Team member"
                    className="w-full h-64 object-cover rounded-lg transition-transform duration-300 hover:scale-105"
                  />
                  <img
                    src="/cybelle/an8j.png"
                    alt="Workshop"
                    className="w-full h-48 object-cover rounded-lg transition-transform duration-300 hover:scale-105"
                  />
                </div>
                <div className="space-y-4 mt-8">
                  <img
                    src="/cybelle/gc.png"
                    alt="Collaboration"
                    className="w-full h-48 object-cover rounded-lg transition-transform duration-300 hover:scale-105"
                  />
                  <img
                    src="/cybelle/logo.png"
                    alt="Conference"
                    className="w-full h-64 object-cover rounded-lg transition-transform duration-300 hover:scale-105"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg border border-gray-200 p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                Stay Connected
              </h2>
              <p className="text-gray-600">
                Get the latest news, resources, and opportunities delivered to your inbox
              </p>
            </div>
            <form className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none"
                />
                <button
                  type="submit"
                  className="px-6 py-3 text-sm font-semibold text-white bg-gray-900 rounded hover:bg-gray-800 transition-colors whitespace-nowrap"
                >
                  Subscribe
                </button>
              </div>
              <p className="text-xs text-gray-500 text-center mt-3">
                By subscribing, you agree to our Privacy Policy and consent to receive updates
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="/cybelle/logo.png" 
                alt="Logo" 
                className="h-24 w-auto mb-4"
              />
              <p className="text-sm leading-relaxed mb-4">
                Empowering women to build successful careers in technology through education, mentorship, and community.
              </p>
              <div className="flex gap-4">
                <a href="#" className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors">
                  <span className="text-xs font-semibold">Li</span>
                </a>
                <a href="#" className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors">
                  <span className="text-xs font-semibold">Tw</span>
                </a>
                <a href="#" className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors">
                  <span className="text-xs font-semibold">Ig</span>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Programs</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Bootcamps</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Mentorship</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Career Services</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Workshops</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Success Stories</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Events</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Community</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Our Team</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Partners</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">
              © 2025 CYBELLE. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-white transition-colors">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CybelleLandingPage;